import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getMonthName } from '../utils/dateUtils';
import { sortEventsByTime, filterEventsForDate } from '../utils/eventUtils';
import EventRow from '../components/EventRow';

import { WEEKDAY_ABBREVIATIONS } from '../constants/options';

export default function MonthScreen({
  viewMonth, viewYear, setViewMonth, setViewYear,
  selectedDate, setSelectedDate,
  allEvents,
  setCurrentScreen,
  openEventDetail,
  todayDay, todayMonth, todayYear,
}) {
  const getFirstDayOfMonth = (month, year) => new Date(year, month - 1, 1).getDay();
  const getDaysInMonthForYear = (month, year) => new Date(year, month, 0).getDate();

  const getEventsForDate = (day, month, year) => {
    return sortEventsByTime(filterEventsForDate(allEvents, day, month, year));
  };

  const getMonthEventDots = (month, year) => {
    const map = {};
    allEvents.filter(e => e.month === month && e.year === year).forEach(e => {
      if (!map[e.day]) map[e.day] = [];
      map[e.day].push({ color: e.dotColor, urgent: e.tags?.includes('Urgent'), title: e.title });
    });
    return map;
  };

  const daysInMonth = getDaysInMonthForYear(viewMonth, viewYear);
  const startDay = getFirstDayOfMonth(viewMonth, viewYear);
  const monthDays = [];
  const monthEventDots = getMonthEventDots(viewMonth, viewYear);

  for (let i = 0; i < startDay; i++) monthDays.push(null);
  for (let day = 1; day <= daysInMonth; day++) monthDays.push(day);

  const navigateMonth = (dir) => {
    let nm = viewMonth + dir, ny = viewYear;
    if (nm > 12) { nm = 1; ny++; }
    if (nm < 1) { nm = 12; ny--; }
    setViewMonth(nm);
    setViewYear(ny);
    setSelectedDate(1);
  };

  const selectedDayEvents = getEventsForDate(selectedDate, viewMonth, viewYear);
  const isTodayDate = (day) => day === todayDay && viewMonth === todayMonth && viewYear === todayYear;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b-2 border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setCurrentScreen('home')}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="text-xl font-bold">{getMonthName(viewMonth)} {viewYear}</h1>
          <div className="flex gap-1">
            <button onClick={() => navigateMonth(-1)} className="p-2 hover:bg-gray-100 rounded-full">
              <ChevronLeft size={20} />
            </button>
            <button onClick={() => navigateMonth(1)} className="p-2 hover:bg-gray-100 rounded-full">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto p-4">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAY_ABBREVIATIONS.map(d => (
            <div key={d} className="text-center text-xs font-semibold text-gray-500 py-2">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {monthDays.map((day, index) => {
            const de = day ? monthEventDots[day] : null;
            const isT = day && isTodayDate(day);
            const hasUrgent = de?.some(e => e.urgent);

            return (
              <button
                key={index}
                onClick={() => { if (day) setSelectedDate(day); }}
                disabled={!day}
                className={`
                  aspect-square p-1 rounded-lg text-left flex flex-col
                  ${!day ? 'invisible' : ''}
                  ${isT ? 'bg-blue-500 text-white font-bold' : 'bg-gray-50 hover:bg-gray-100'}
                  ${hasUrgent && !isT ? 'ring-2 ring-red-400' : ''}
                  ${selectedDate === day && !isT ? 'ring-2 ring-blue-400' : ''}
                `}
              >
                {day && (
                  <>
                    <div className={`text-xs mb-1 ${isT ? 'text-white' : 'text-gray-900'}`}>{day}</div>
                    {de && (
                      <div className="flex flex-col gap-0.5 mt-auto overflow-hidden">
                        {de.slice(0, 2).map((ev, i) => (
                          <div key={i} className={`text-[8px] leading-tight truncate rounded px-0.5 ${isT ? 'bg-white/30 text-white' : `${ev.color} text-gray-800`}`}>
                            {ev.title}
                          </div>
                        ))}
                        {de.length > 2 && (
                          <div className={`text-[8px] leading-tight ${isT ? 'text-white/80' : 'text-gray-500'}`}>+{de.length - 2} more</div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected Day's Events */}
        <div className="mt-6 p-4 bg-white border-2 border-gray-200 rounded-lg">
          <div className="text-sm font-semibold text-gray-700 mb-3">
            {isTodayDate(selectedDate) ? "Today's Events" : `Events on ${getMonthName(viewMonth)} ${selectedDate}`}
          </div>
          <div className="space-y-2">
            {selectedDayEvents.length > 0 ? (
              selectedDayEvents.map(event => (
                <EventRow key={event.id} event={event} onClick={() => openEventDetail(event)} />
              ))
            ) : (
              <div className="text-center py-8">
                <div className="text-2xl mb-2">🎉</div>
                <div className="text-gray-900 font-semibold text-lg mb-1">Free day!</div>
                <div className="text-gray-500 text-sm">No events scheduled</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
